import { IconPencil, IconPlus, IconX } from '@tabler/icons-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '@/components/common';
import type { Entry, IcdRef, Visit } from '@/types/api';
import { suggestDiagnoses } from '@/utils/diagnosisSuggestions';
import { Diagnoses } from './HealthSheet';

type Patch = { anamnesis?: string | null; diagnoses?: string[] };

type Props = {
  visit: Visit;
  entries: Entry[];
  /** Saves the change; resolves true when saved. */
  onSave: (visit: Visit, patch: Patch) => Promise<boolean>;
};

function AnamnesisEditor({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (text: string | null) => Promise<boolean>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(initial);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSave(text.trim() || null);
    setBusy(false);
    if (ok) onCancel();
  };
  return (
    <form className="stack" onSubmit={submit}>
      <textarea
        id="visit-anamnesis"
        className="textarea"
        aria-label={t('chart.visitRecord.anamnesis')}
        maxLength={4000}
        // biome-ignore lint/a11y/noAutofocus: opened by the edit button, typing comes next
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="row">
        <button type="submit" className="btn btn--sm btn--dark" disabled={busy}>
          {t('common.save')}
        </button>
        <button type="button" className="btn btn--sm" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

function DiagnosesSheet({
  initial,
  onSave,
  onClose,
}: {
  initial: IcdRef[];
  onSave: (codes: string[]) => Promise<boolean>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  return (
    <Sheet title={t('chart.visitRecord.diagnoses')} onClose={onClose}>
      <div className="stack">
        <Diagnoses
          id="visit-icd-results"
          label={t('chart.visitRecord.diagnoses')}
          value={value}
          onChange={setValue}
        />
        <button
          type="button"
          className="btn btn--dark btn--block"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            const ok = await onSave(value.map((d) => d.code));
            setBusy(false);
            if (ok) onClose();
          }}
        >
          {t('common.save')}
        </button>
      </div>
    </Sheet>
  );
}

/**
 * What the protocol records of the visit (#23): the protocol number, the
 * anamnesis and the diagnoses, with the codes its findings suggest one tap away.
 */
export function VisitRecord({ visit, entries, onSave }: Props) {
  const { t } = useTranslation();
  const [editingAnamnesis, setEditingAnamnesis] = useState(false);
  const [editingDiagnoses, setEditingDiagnoses] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const suggested = suggestDiagnoses(entries, visit.diagnoses);
  const codes = visit.diagnoses.map((d) => d.code);

  return (
    <div className="visit-record">
      <div className="row">
        <span className="field__label grow">{t('chart.visitRecord.anamnesis')}</span>
        <span className="small muted">
          {t('chart.visitRecord.protocolNumber', { number: visit.protocolNumber })}
        </span>
        {!editingAnamnesis && (
          <button
            type="button"
            className="icon-btn icon-btn--sm"
            onClick={() => setEditingAnamnesis(true)}
            aria-label={t('chart.visitRecord.editAnamnesis')}
            title={t('chart.visitRecord.editAnamnesis')}
          >
            <IconPencil size={16} />
          </button>
        )}
      </div>
      {editingAnamnesis ? (
        <AnamnesisEditor
          initial={visit.anamnesis ?? ''}
          onSave={(anamnesis) => onSave(visit, { anamnesis })}
          onCancel={() => setEditingAnamnesis(false)}
        />
      ) : visit.anamnesis ? (
        <p className="visit-record__text">{visit.anamnesis}</p>
      ) : (
        <p className="small muted">{t('chart.visitRecord.noAnamnesis')}</p>
      )}

      <div className="row">
        <span className="field__label grow">{t('chart.visitRecord.diagnoses')}</span>
        <button
          type="button"
          className="icon-btn icon-btn--sm"
          onClick={() => setEditingDiagnoses(true)}
          aria-label={t('chart.visitRecord.editDiagnoses')}
          title={t('chart.visitRecord.editDiagnoses')}
        >
          <IconPencil size={16} />
        </button>
      </div>
      {visit.diagnoses.length > 0 ? (
        <ul className="icd-list icd-list--compact">
          {visit.diagnoses.map((d) => (
            <li key={d.code} className="icd-list__item">
              <span className="icd-code">{d.code}</span>
              <span className="grow">{d.name}</span>
              <button
                type="button"
                className="icon-btn icon-btn--sm"
                onClick={() => onSave(visit, { diagnoses: codes.filter((c) => c !== d.code) })}
                aria-label={t('chart.health.remove', { name: d.code })}
              >
                <IconX size={16} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="small muted">{t('chart.visitRecord.noDiagnoses')}</p>
      )}
      {suggested.length > 0 && (
        <div className="visit-record__suggested">
          <span className="small muted">{t('chart.visitRecord.suggested')}</span>
          {suggested.map((d) => (
            <button
              key={d.code}
              type="button"
              className="chip chip--suggest"
              disabled={adding !== null}
              title={d.name}
              onClick={async () => {
                setAdding(d.code);
                await onSave(visit, { diagnoses: [...codes, d.code] });
                setAdding(null);
              }}
            >
              <IconPlus size={14} aria-hidden="true" />
              <span className="icd-code">{d.code}</span> {d.name}
            </button>
          ))}
        </div>
      )}
      {editingDiagnoses && (
        <DiagnosesSheet
          initial={visit.diagnoses}
          onSave={(diagnoses) => onSave(visit, { diagnoses })}
          onClose={() => setEditingDiagnoses(false)}
        />
      )}
    </div>
  );
}
